import React, { useState } from "react";
import {
  Spinner,
  LoadingButton,
  useStateContext,
} from "@veda/dashboard-ui";
import { BsBellSlash, BsCheckAll, BsCircleFill } from "react-icons/bs";
import { usePppApi } from "../services/pppApi";
import { useNotifiche } from "../hooks/useNotifiche";

const TIPO_LABELS = {
  nuova_proposta:               "Nuova proposta",
  proposta_valutata:            "Proposta valutata",
  interesse_pubblico_dichiarato:"Interesse pubblico dichiarato",
  interesse_pubblico_pubblicato:"Interesse pubblico pubblicato",
  nuova_richiesta_integrazione: "Richiesta integrazione",
  integrazione_fornita:         "Integrazione fornita",
  provvedimento_emesso:         "Provvedimento emesso",
  scadenza_imminente:           "Scadenza imminente",
  nuovo_documento:              "Nuovo documento",
  cambio_stato_procedimento:    "Cambio stato procedimento",
  nuovo_verbale:                "Nuovo verbale",
  procedimento_assegnato:       "Procedimento assegnato",
};

const TIPO_COLORS = {
  scadenza_imminente:           "text-red-500",
  provvedimento_emesso:         "text-purple-500",
  procedimento_assegnato:       "text-blue-500",
};

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("it-IT", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function NotificaRow({ notifica, onMarcaLetta }) {
  const dotColor = TIPO_COLORS[notifica.tipo] ?? "text-gray-400";
  return (
    <div
      className={`flex gap-3 p-4 border-b last:border-b-0 transition-colors ${
        notifica.letta ? "bg-white" : "bg-blue-50"
      }`}
    >
      <div className="pt-1 flex-shrink-0">
        <BsCircleFill
          size={8}
          className={notifica.letta ? "text-gray-200" : dotColor || "text-blue-400"}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className={`text-sm font-medium ${notifica.letta ? "text-gray-700" : "text-gray-900"}`}>
              {notifica.titolo}
            </p>
            {notifica.procedimento_titolo && (
              <p className="text-xs text-gray-500 mt-0.5">{notifica.procedimento_titolo}</p>
            )}
          </div>
          <span className="text-xs text-gray-400 flex-shrink-0">{formatDate(notifica.created_at)}</span>
        </div>
        <p className="text-sm text-gray-600 mt-1">{notifica.messaggio}</p>
        <div className="flex items-center gap-3 mt-2">
          <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">
            {TIPO_LABELS[notifica.tipo] ?? notifica.tipo}
          </span>
          {!notifica.letta && (
            <button
              onClick={() => onMarcaLetta(notifica.id)}
              className="text-xs text-blue-600 hover:underline"
            >
              Segna come letta
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function NotifichePage() {
  const api = usePppApi();
  const { addToast, _locate } = useStateContext();
  const [soloNonLette, setSoloNonLette] = useState(false);
  const [marcandoTutte, setMarcandoTutte] = useState(false);

  const { notifiche, pagination, loading, refresh } = useNotifiche({ nonLette: soloNonLette });

  function handleMarcaLetta(id) {
    api.marcaNotificaLetta(id, {
      onSuccess: () => refresh(),
    });
  }

  function handleMarcaTutteLette() {
    setMarcandoTutte(true);
    api.marcaTutteNotificheLette({
      onSuccess: () => {
        addToast({ message: _locate("ppp_notifiche_aggiornate"), type: "success" });
        refresh();
        setMarcandoTutte(false);
      },
      onFormError: () => setMarcandoTutte(false),
    });
  }

  const nonLetteCount = notifiche.filter((n) => !n.letta).length;

  return (
    <div className="w-full py-8 px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            {_locate("ppp_notifiche_titolo")}
            {nonLetteCount > 0 && (
              <span className="ml-2 text-sm font-normal text-blue-600">
                ({nonLetteCount} {_locate("ppp_notifiche_non_lette")})
              </span>
            )}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            role="switch"
            aria-checked={soloNonLette}
            onClick={() => setSoloNonLette((v) => !v)}
            title="Mostra solo le notifiche non ancora lette"
            className="flex items-center gap-2.5 text-sm text-gray-600 select-none focus:outline-none"
          >
            {_locate("ppp_notifiche_solo_non_lette")}
            <span
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors duration-200 ${
                soloNonLette ? "bg-blue-600" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 ${
                  soloNonLette ? "translate-x-5" : "translate-x-0.5"
                }`}
              />
            </span>
          </button>
          <span
            title={
              nonLetteCount === 0
                ? "Nessuna notifica da segnare come letta"
                : `Segna come lette tutte le ${nonLetteCount} notifiche non lette`
            }
            className={nonLetteCount === 0 ? "opacity-50 pointer-events-none" : ""}
          >
            <LoadingButton
              isLoading={marcandoTutte}
              customFunc={handleMarcaTutteLette}
              icon={<BsCheckAll size={16} />}
              text={_locate("ppp_notifiche_marca_tutte_lette")}
            />
          </span>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : notifiche.length === 0 ? (
        <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
          <div className="flex flex-col items-center gap-3 py-16 text-gray-400">
            <BsBellSlash size={36} />
            <p className="text-sm">
              {soloNonLette
                ? _locate("ppp_notifiche_tutte_lette")
                : _locate("ppp_notifiche_nessuna")}
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
          {notifiche.map((n) => (
            <NotificaRow key={n.id} notifica={n} onMarcaLetta={handleMarcaLetta} />
          ))}
        </div>
      )}

      {pagination && pagination.lastPage > 1 && (
        <p className="text-xs text-gray-500 text-center mt-4">
          Pagina {pagination.currentPage} di {pagination.lastPage} — {pagination.total} notifiche totali
        </p>
      )}
    </div>
  );
}
